/**
 * Ofertat — regjistrime me cikël të vetin.
 *
 * «Ofertë» ishte fjalë te statusi i aplikimit: pa pagë, pa datë fillimi, pa
 * afat, pa miratim. Nuk dallohej një ofertë e dërguar nga një e refuzuar, dhe
 * askush s'e dinte sa oferta prisnin miratim.
 *
 * Aplikimi mban FAZËN; oferta mban dokumentin dhe jetën e tij. Prandaj një
 * ofertë e refuzuar nuk e kthen vetvetiu kandidatin — ai vendim është i HR-së.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Typography, Stack, Chip, Button, Tooltip, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, Divider,
} from '@mui/material';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PageHeader from '../components/common/PageHeader';
import usePermissions from '../hooks/usePermissions';
import { authFetch } from '../services/apiServices';
import { OFFER_STATUSES, offerStatusMeta, employmentTypeLabel } from '../config/recruitmentPipeline';
import { printOffer } from '../utils/offerPrint';

const C = { ink: '#0F172A', ink500: '#64748B', line: '#E5E7EB', surface: '#F8FAFC' };

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (isNaN(x)) return '—';
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};
const fmtMoney = (n, cur) => (n === null || n === undefined ? '—' : `${Number(n).toLocaleString('de-DE')} ${cur || 'EUR'}`);

/**
 * Veprimet e mundshme sipas gjendjes — të njëjtat kalime që zbaton serveri.
 * Një buton që do të refuzohej nuk shfaqet; por serveri vendos gjithsesi.
 */
const ACTIONS = {
  draft:            [{ path: 'submit',  label: 'Dërgo për miratim' }],
  pending_approval: [{ path: 'approve', label: 'Mirato', perm: 'approve' },
                     { path: 'approve', label: 'Kthe te drafti', body: { decision: 'reject' }, perm: 'approve' }],
  approved:         [{ path: 'send',    label: 'Shëno si të dërguar' }],
  sent:             [{ path: 'respond', label: 'Pranoi', body: { answer: 'accepted' } },
                     { path: 'respond', label: 'Refuzoi', decline: true }],
};

export default function OffersPage() {
  const { can } = usePermissions();
  const canManage = can('recruitment.create') || can('recruitment.edit') || can('recruitment.manage');
  const canApprove = can('recruitment.approve') || can('recruitment.manage');

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [decline, setDecline] = useState(null);   // { id, reason }
  // Bllokuesi i pop-up-eve e ndal dritaren pa asnjë shenjë; pa këtë mesazh,
  // butoni do të dukej i prishur.
  const [printBlocked, setPrintBlocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await authFetch(`/api/offers${status !== 'all' ? `?status=${status}` : ''}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      setRows(j.data || []);
    } catch (e) { setError(e?.message || 'Ofertat nuk u lexuan.'); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body) => {
    setBusy(`${id}:${path}`); setError('');
    try {
      const r = await authFetch(`/api/offers/${id}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      setDecline(null);
      await load();
    } catch (e) { setError(e?.message || 'Veprimi dështoi.'); }
    finally { setBusy(null); }
  };

  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <Box>
      <PageHeader title="Ofertat" subtitle="Ofertat e punës, nga drafti te pranimi — me miratimin e tyre." />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField select size="small" label="Statusi" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="all">Të gjitha</MenuItem>
          {OFFER_STATUSES.map((s) => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {Object.entries(counts).map(([k, v]) => (
          <Chip key={k} size="small" label={`${offerStatusMeta(k).label}: ${v}`} color={offerStatusMeta(k).color} variant="outlined" />
        ))}
      </Stack>

      <Card elevation={0} sx={{ border: `1px solid ${C.line}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 2, textAlign: 'center' }}>
              <WorkOutlineRoundedIcon sx={{ fontSize: 34, color: '#94A3B8' }} />
              <Typography sx={{ fontWeight: 700, color: C.ink }}>Ende asnjë ofertë.</Typography>
              <Typography variant="body2" color="text.secondary">
                Oferta krijohet nga dosja e kandidatit, te «Kandidatët».
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: C.surface }}>
                    <TableCell sx={{ fontWeight: 700 }}>Kandidati</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pozita</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Paga</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Angazhimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fillimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Skadon</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const st = offerStatusMeta(r.status);
                    const actions = (ACTIONS[r.status] || []).filter((a) => (a.perm === 'approve' ? canApprove : canManage));
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.candidateName || '—'}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: C.ink500 }}>Oferta #{r.id}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.position || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtMoney(r.salary, r.currency)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{employmentTypeLabel(r.employmentType)}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.startDate)}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.expiresOn)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={st.label} color={st.color} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                          {r.status === 'declined' && r.declineReason && (
                            <Tooltip title={r.declineReason} arrow>
                              <Typography sx={{ fontSize: 11, color: C.ink500, mt: 0.25 }}>arsyeja ▾</Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            {/* Dokumenti printohet në çdo gjendje, por vetëm ai i
                                dërguar del pa vulën DRAFT — një draft i printuar
                                nuk duhet të lexohet si premtim. */}
                            <Tooltip title="Dokumenti i ofertës">
                              <Button size="small" variant="text" startIcon={<PrintOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                                onClick={() => { if (!printOffer(r)) setPrintBlocked(true); }}
                                sx={{ textTransform: 'none', fontSize: '.75rem', minWidth: 0 }}>
                                Printo
                              </Button>
                            </Tooltip>
                            {actions.map((a, i) => (
                              <Button key={i} size="small" variant={i === 0 ? 'outlined' : 'text'}
                                disabled={busy === `${r.id}:${a.path}`}
                                onClick={() => (a.decline ? setDecline({ id: r.id, reason: '' }) : act(r.id, a.path, a.body))}
                                sx={{ textTransform: 'none', fontSize: '.75rem' }}>
                                {a.label}
                              </Button>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {printBlocked && (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setPrintBlocked(false)}>
          Shfletuesi e bllokoi dritaren e printimit. Lejo dritaret kërcyese për këtë faqe dhe provo sërish.
        </Alert>
      )}

      {/* Refuzimi nga kandidati: arsyeja ruhet, sepse ajo i duhet raportit të
          pranimit të ofertave — dhe sepse pyetja «pse na refuzojnë» ka përgjigje. */}
      <Dialog open={Boolean(decline)} onClose={() => setDecline(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Kandidati e refuzoi ofertën</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Arsyeja (opsionale)" multiline minRows={2} fullWidth size="small"
              placeholder="p.sh. pranoi ofertë tjetër, paga, vendndodhja…"
              value={decline?.reason || ''}
              onChange={(e) => setDecline((p) => ({ ...p, reason: e.target.value }))} />
            <Divider />
            <Alert severity="info" sx={{ py: 0.25, fontSize: '.8rem' }}>
              Kandidati nuk refuzohet vetvetiu. Faza e tij ndryshohet veçmas, te dosja.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDecline(null)} color="inherit">Anulo</Button>
          <Button variant="contained" color="error"
            onClick={() => act(decline.id, 'respond', { answer: 'declined', reason: decline.reason })}>
            Shëno si të refuzuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
